import { Building2, Users, Zap, CheckCircle2, AlertTriangle, AlertCircle, Package } from 'lucide-react';

interface ImportInsightsProps {
  accounts: number;
  properties: number;
  vendors: number;
  units: number;
  success: number;
  warning: number;
  failure: number;
  total: number;
}

/**
 * ImportInsights - Ultra compact horizontal layout for 100% screen fit
 */
export default function ImportInsights({
  accounts,
  properties,
  vendors,
  units,
  success,
  warning,
  failure,
  total,
}: ImportInsightsProps) {
  const successPercent = total > 0 ? Math.round((success / total) * 100) : 0;
  const warningPercent = total > 0 ? Math.round((warning / total) * 100) : 0;
  const failurePercent = total > 0 ? Math.round((failure / total) * 100) : 0;

  return (
    <div className="px-3 py-1.5 bg-muted/20 border-b border-border">
      <div className="flex items-center justify-between gap-2">
        {/* Unique Counts - Ultra compact pills */}
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200/50 dark:border-blue-800/50">
            <Building2 className="h-3 w-3 text-blue-600" />
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">{properties}</span>
            <span className="text-[10px] text-blue-600/80">Prop</span>
          </div>

          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-950/30 rounded border border-green-200/50 dark:border-green-800/50">
            <Zap className="h-3 w-3 text-green-600" />
            <span className="text-xs font-semibold text-green-700 dark:text-green-300">{accounts}</span>
            <span className="text-[10px] text-green-600/80">Acct</span>
          </div>

          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-950/30 rounded border border-purple-200/50 dark:border-purple-800/50">
            <Package className="h-3 w-3 text-purple-600" />
            <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">{vendors}</span>
            <span className="text-[10px] text-purple-600/80">Vend</span>
          </div>

          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/30 rounded border border-indigo-200/50 dark:border-indigo-800/50">
            <Users className="h-3 w-3 text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">{units}</span>
            <span className="text-[10px] text-indigo-600/80">Unit</span>
          </div>
        </div>

        {/* Status Summary with Progress */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-[10px]">
            <div className="flex items-center gap-0.5">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              <span className="font-medium text-emerald-600">{success}</span>
            </div>
            {warning > 0 && (
              <div className="flex items-center gap-0.5">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                <span className="font-medium text-amber-600">{warning}</span>
              </div>
            )}
            {failure > 0 && (
              <div className="flex items-center gap-0.5">
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span className="font-medium text-red-600">{failure}</span>
              </div>
            )}
          </div>
          
          {/* Mini Progress Bar */}
          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden flex">
            {successPercent > 0 && (
              <div className="h-full bg-emerald-500" style={{ width: `${successPercent}%` }} />
            )}
            {warningPercent > 0 && (
              <div className="h-full bg-amber-500" style={{ width: `${warningPercent}%` }} />
            )}
            {failurePercent > 0 && (
              <div className="h-full bg-red-500" style={{ width: `${failurePercent}%` }} />
            )}
          </div>
          
          <span className="text-[10px] text-muted-foreground">{total} rows</span>
        </div>
      </div>
    </div>
  );
}
